import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiCheck, FiTrash2, FiX } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import "./LineTemplate.css";
import { storeUserLinks, LINK_TITLE_ALREADY_EXISTS } from "../../../../../../Utils/SupabaseServices/UserLinksDatabase";
import {
  handleProductFileUpload,
  ACCEPTED_IMAGE_TYPES,
  uploadImageFile,
} from "../../../../../../Utils/uploadImage/UploadImage";
import { Upload, UploadProps, Spin, message } from "antd";
import { DeleteConfirmation } from "../LinksComponentDashboard";
import { Input, Popover } from "antd";
import { deleteUserLinks } from "../../../../../../Utils/SupabaseServices/UserLinksDatabase";
import { addHttpToLink } from "../../../../../../Utils/StayUpToDate/RenderComponent";
import { useDesign } from "../../../../../../context/DesignContext";
import { getHoverColor } from "./hoverUtils";
import {
  getLinkNameError,
  MAX_LINK_NAME_CHARS,
  trimLinkName,
} from "../linksSectionLimits";
import { useImageCropper } from "../../../../../../hooks/useImageCropper";
import {
  LINK_LINE_CROP,
  type CropViewportState,
} from "../../../../../../Utils/imageCrop/cropImageFile";
import {
  buildPersistedImageCrop,
  getDistinctLinkOriginalUrl,
  getLinkLayoutCropEntry,
  getLinkLayoutDisplayUrl,
  hasLinkOriginalImageChanged,
  isCropViewportCompatible,
  mergeLinkLayoutImageCrop,
  resolveLinkLayoutCropState,
  viewportFromPreset,
} from "../../../../../../Utils/imageCrop/cropStatePersist";
import ImageCropperModal from "../../../../../../components/ImageCropper/ImageCropperModal";
import {
  bannerImageFrameStyle,
  readLinkCoverFrame,
} from "../../../../MasterTemplateEditor/templateBannerUtils";

/** Line links use circular/square LINK_LINE_CROP — keep viewport separate from grid. */
const LINE_LINK_CROP_VIEWPORT = viewportFromPreset(LINK_LINE_CROP);
const LINK_LAYOUT = "line" as const;

function seedLineImageUrl(card: any) {
  return getLinkLayoutDisplayUrl(
    card?.image_crop,
    LINK_LAYOUT,
    card?.image_url,
    card?.original_image_url
  );
}

function lineCoverStyle(card: any): React.CSSProperties {
  if (getLinkLayoutCropEntry(card?.image_crop, LINK_LAYOUT)) return {};
  const frame = readLinkCoverFrame(card?.image_crop);
  return frame ? bannerImageFrameStyle(frame) || {} : {};
}

function seedLineCropState(card: any) {
  return resolveLinkLayoutCropState(
    card?.image_crop,
    LINK_LAYOUT,
    LINE_LINK_CROP_VIEWPORT
  );
}

const LineTemplate = (props: any) => {
  const {
    card,
    onRemove,
    username,
    user_id,
    componentName,
    isDashboard,
    refetch,
    brand_color,
    setEditIndex,
    editIndex,
    templates,
    handleRedirect,
    from,
    componentId,
    pageFrom,
    designTokens,
    isDragging = false,
    dragHandleProps,
    isMobileView,
    isLayoutCropProcessing = false,
  } = props;
  const [title, setTitle] = useState(() => card?.link_name || "");
  const [link, setLink] = useState(() => card?.link_url || "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(() =>
    seedLineImageUrl(card)
  );
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(
    () => card?.original_image_url || null
  );
  const [linkCropState, setLinkCropState] = useState<CropViewportState | null>(
    () => seedLineCropState(card)
  );
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationErrorLink, setValidationErrorLink] = useState<string | null>(
    null
  );
  const [isNewCard, setIsNewCard] = useState(card.id === 0);
  const onImageUploadStatusChange = props.onImageUploadStatusChange;

  const isEditing = editIndex === card.id;

  const {
    cropperOpen,
    cropperImageSrc,
    initialCropState,
    isLoadingOriginal,
    originalSourceUrl,
    closeCropper,
    cancelCropper,
    commitCropSession,
    setOriginalSourceUrl,
    recordCropViewportState,
    openCropperForFile,
    editOriginalImage,
    handleCropReplace,
    replaceInputRef,
  } = useImageCropper({
    aspectRatio: LINK_LINE_CROP.aspectRatio,
    viewportWidth: LINE_LINK_CROP_VIEWPORT.width,
    viewportHeight: LINE_LINK_CROP_VIEWPORT.height,
    defaultFit: "cover",
  });

  useEffect(() => {
    // While this card is open for edit, local state owns image/original — do not
    // clobber an in-session original upload with stale card.original_image_url.
    if (editIndex === card.id) {
      return;
    }
    if (!isNewCard || card.link_name || card.link_url) {
      setTitle(card.link_name);
      setLink(card.link_url);
      setImageUrl(seedLineImageUrl(card));
      setOriginalImageUrl(card.original_image_url || null);
      setLinkCropState(seedLineCropState(card));
    }
  }, [card, editIndex, isNewCard]);

  useEffect(() => {
    // Seed/reset only when the edited card identity changes — not on every
    // card prop refresh (that was wiping in-session original_image_url).
    setTitle(card.link_name || "");
    setLink(card.link_url || "");
    setImageUrl(seedLineImageUrl(card));
    setOriginalImageUrl(card.original_image_url || null);
    setLinkCropState(seedLineCropState(card));
    setValidationError(null);
    setValidationErrorLink(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally seed on edit target change only
  }, [editIndex, card.id]);

  useEffect(() => {
    if (props.image_url && !imageUrl) {
      setImageUrl(props.image_url);
    }
  }, [props.image_url]);

  // Same as Highlights: seed cropper from the persisted uncropped original only.
  useEffect(() => {
    if (originalImageUrl) {
      setOriginalSourceUrl(originalImageUrl);
      return;
    }
    if (!imageUrl) {
      setOriginalSourceUrl("");
    }
  }, [originalImageUrl, imageUrl, setOriginalSourceUrl]);

  const isValidUrl = (urlString: string) => {
    try {
      // Always add protocol if missing
      const url = new URL(
        urlString.startsWith("http") ? urlString : "https://" + urlString
      );
      if (!url.hostname.includes(".")) return false;
      setValidationErrorLink("");
      return true;
    } catch (err) {
      setValidationErrorLink("Please enter a valid URL.");
      return false;
    }
  };

  const handleSave = async (e: any) => {
    e.stopPropagation();

    const titleError = getLinkNameError(title);
    if (titleError) {
      setValidationError(titleError);
      return;
    }

    const trimmedTitle = trimLinkName(title).trim();

    if (!link || !link.trim()) {
      setValidationErrorLink("A link is required.");
      return;
    }

    // URL validation
    let urlToValidate = link.trim();
    if (!isValidUrl(urlToValidate)) {
      setValidationErrorLink("Please enter a valid URL.");
      return;
    }

    const duplicateTitle = templates.find(
      (template: any) =>
        template.link_name.toLowerCase() === trimmedTitle.toLowerCase() &&
        template.id !== card.id
    );

    if (duplicateTitle) {
      setValidationError(LINK_TITLE_ALREADY_EXISTS);
      return;
    }

    try {
      const built = buildPersistedImageCrop(
        linkCropState,
        LINE_LINK_CROP_VIEWPORT
      );
      const existingEntry = getLinkLayoutCropEntry(
        card.image_crop,
        LINK_LAYOUT
      );
      const existingMatchesLayout =
        Boolean(existingEntry) &&
        (existingEntry!.cropViewport.width === 0 ||
          isCropViewportCompatible(
            existingEntry!.cropViewport,
            LINE_LINK_CROP_VIEWPORT
          ));
      // Prefer live session original; never wipe DB with null while an image remains.
      const originalToPersist =
        originalImageUrl ||
        originalSourceUrl ||
        card.original_image_url ||
        null;
      const originalMergeOptions = {
        previousOriginalUrl: card.original_image_url,
        nextOriginalUrl: !imageUrl ? null : originalToPersist,
      };
      // Omit crop write on title-only saves when this layout has no matching crop yet
      // (e.g. legacy flat crop belongs to the other aspect). Never invent a default.
      // When original URL changes, merge drops the opposite layout (grid) entry.
      let persistedCrop:
        | ReturnType<typeof mergeLinkLayoutImageCrop>
        | undefined;
      if (!imageUrl) {
        persistedCrop = mergeLinkLayoutImageCrop(
          card.image_crop,
          LINK_LAYOUT,
          null,
          LINE_LINK_CROP_VIEWPORT,
          originalMergeOptions
        );
      } else if (built || existingMatchesLayout) {
        const layoutEntry = built
          ? { ...built, image_url: imageUrl }
          : {
              cropState: existingEntry!.cropState,
              cropViewport:
                existingEntry!.cropViewport.width > 0
                  ? existingEntry!.cropViewport
                  : LINE_LINK_CROP_VIEWPORT,
              image_url: imageUrl,
            };
        persistedCrop = mergeLinkLayoutImageCrop(
          card.image_crop,
          LINK_LAYOUT,
          layoutEntry,
          LINE_LINK_CROP_VIEWPORT,
          originalMergeOptions
        );
      } else if (
        imageUrl &&
        hasLinkOriginalImageChanged(
          card.original_image_url,
          originalToPersist
        )
      ) {
        // Original replaced but no crop payload — still drop stale grid data.
        persistedCrop = mergeLinkLayoutImageCrop(
          card.image_crop,
          LINK_LAYOUT,
          null,
          LINE_LINK_CROP_VIEWPORT,
          originalMergeOptions
        );
      }
      const originalArg = !imageUrl
        ? null
        : originalToPersist || undefined;
      const result = await storeUserLinks(
        card.id === 0 ? null : card.id,
        user_id,
        username,
        trimmedTitle,
        urlToValidate,
        imageUrl,
        componentName,
        from,
        componentId,
        originalArg,
        persistedCrop
      );

      setValidationError(null);
      setValidationErrorLink(null);
      setIsNewCard(false);
      setEditIndex(undefined);
      refetch();
    } catch (error) {
      console.error("Error saving link:", error);
      const msg = error instanceof Error ? error.message : "";
      setValidationError(
        msg === LINK_TITLE_ALREADY_EXISTS
          ? LINK_TITLE_ALREADY_EXISTS
          : "Failed to save link. Please try again.",
      );
    }
  };

  const handleDelete = async () => {
    try {
      if (card.id !== 0) {
        await deleteUserLinks(user_id, componentName, card.id, from, componentId);
      }
      onRemove(card.id);
      setShowDeleteConfirm(false);
      setEditIndex(undefined);
      refetch();
    } catch (error) {
      console.error("Error deleting link:", error);
    }
  };

  const uploadCroppedLinkImage = useCallback(
    async (file: File) => {
      setUploading(true);
      onImageUploadStatusChange?.(true);

      try {
        const response = await handleProductFileUpload({
          info: { file, fileList: [] },
          username,
          productName: "",
          setFileList,
          setProductImage: setImageUrl,
          storageName: "mylinks",
        });

        if (response?.publicUrl) {
          setImageUrl(response.publicUrl);
          props.onChange?.({
            image_url: response.publicUrl,
            original_image_url:
              originalImageUrl || originalSourceUrl || null,
          });
        }
      } catch {
        message.error("Failed to upload image. Please try again.");
      } finally {
        setUploading(false);
        onImageUploadStatusChange?.(false);
      }
    },
    [
      onImageUploadStatusChange,
      originalImageUrl,
      originalSourceUrl,
      props,
      username,
    ]
  );

  const handleCroppedImageSave = useCallback(
    async (file: File, cropState: CropViewportState) => {
      // Always keep the uncropped source so the next Edit opens the same framing.
      const sourceForPersist =
        originalImageUrl || originalSourceUrl || null;
      if (sourceForPersist) {
        setOriginalImageUrl(sourceForPersist);
        if (sourceForPersist !== originalSourceUrl) {
          setOriginalSourceUrl(sourceForPersist);
        }
      }
      recordCropViewportState(cropState);
      setLinkCropState(cropState);
      commitCropSession();
      closeCropper();
      await uploadCroppedLinkImage(file);
    },
    [
      closeCropper,
      commitCropSession,
      originalImageUrl,
      originalSourceUrl,
      recordCropViewportState,
      setOriginalSourceUrl,
      uploadCroppedLinkImage,
    ]
  );

  const handleSelectImageFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        message.error("You can only upload image files!");
        return;
      }

      try {
        setUploading(true);
        onImageUploadStatusChange?.(true);
        const uploadedOriginalUrl = await uploadImageFile({
          file,
          username,
          storageName: "mylinks",
        });
        setOriginalImageUrl(uploadedOriginalUrl);
        setOriginalSourceUrl(uploadedOriginalUrl);
        setLinkCropState(null);
        openCropperForFile(file);
      } catch {
        commitCropSession();
        message.error("Failed to load image. Please try again.");
      } finally {
        setUploading(false);
        onImageUploadStatusChange?.(false);
      }
    },
    [
      commitCropSession,
      onImageUploadStatusChange,
      openCropperForFile,
      setOriginalSourceUrl,
      username,
    ]
  );

  const handleOpenLinkCropper = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      const currentUrl = imageUrl || "";
      if (!currentUrl) {
        replaceInputRef.current?.click();
        return;
      }
      // Crop state is relative to the uncropped bitmap — never restore it on
      // the already-exported display URL (causes extreme / random framing).
      const distinctOriginal = getDistinctLinkOriginalUrl(
        currentUrl,
        originalImageUrl,
        originalSourceUrl,
        card.original_image_url
      );
      const sourceUrl = distinctOriginal || currentUrl;
      void editOriginalImage({
        croppedImageUrl: currentUrl,
        sourceUrl,
        cropState: distinctOriginal ? linkCropState : null,
        // Display-only rows: contain @ 1 so the modal matches the thumb
        // (hook defaultFit cover would invent zoom on the cropped file).
        defaultFit: distinctOriginal ? undefined : "contain",
      });
    },
    [
      card.original_image_url,
      editOriginalImage,
      imageUrl,
      linkCropState,
      originalImageUrl,
      originalSourceUrl,
      replaceInputRef,
    ]
  );

  const handleCropperCancel = useCallback(() => {
    cancelCropper();
    setUploading(false);
    onImageUploadStatusChange?.(false);
  }, [cancelCropper, onImageUploadStatusChange]);

  const handleRemoveImage = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setImageUrl(null);
      setOriginalImageUrl(null);
      setLinkCropState(null);
      setOriginalSourceUrl("");
    },
    [setOriginalSourceUrl]
  );

  const beforeUpload = useCallback(
    (file: File) => {
      if (uploading || cropperOpen || isLoadingOriginal) {
        return Upload.LIST_IGNORE;
      }
      if (!file.type.startsWith("image/")) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      void handleSelectImageFile(file);
      return false;
    },
    [cropperOpen, handleSelectImageFile, isLoadingOriginal, uploading]
  );

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: ACCEPTED_IMAGE_TYPES,
    beforeUpload,
    showUploadList: false,
    disabled: uploading || cropperOpen || isLoadingOriginal,
  };

  const isImageBusy =
    uploading ||
    cropperOpen ||
    isLoadingOriginal ||
    Boolean(isLayoutCropProcessing);

  const handleCancelSave = () => {
    refetch();
    setTitle(card.link_name);
    setLink(card.link_url);
    setImageUrl(seedLineImageUrl(card));
    setOriginalImageUrl(card.original_image_url || null);
    setLinkCropState(seedLineCropState(card));
    setEditIndex(undefined);

    setValidationError("");
    setValidationErrorLink("");
  };

  const handleEditClick = (e: any) => {
    if (isDragging) return;
    e.stopPropagation();
    // If this card is already being edited, do nothing
    if (editIndex === card.id) {
      return;
    }
    // Reset any validation errors
    setValidationError(null);
    setValidationErrorLink(null);
    // Reset values to original card values
    setTitle(card.link_name || "");
    setLink(card.link_url || "");
    setImageUrl(seedLineImageUrl(card));
    setOriginalImageUrl(card.original_image_url || null);
    setLinkCropState(seedLineCropState(card));
    // Set the new editIndex
    setEditIndex(card.id);
  };

  const {
    templateName,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    fontFamily,
    primaryFont,
    secondaryFont,
    forthColor,
  } = useDesign();
  // When no explicit designTokens prop is provided (e.g. the dashboard Link in
  // Bio editor), fall back to the live DesignContext so color/font changes from
  // the design panel reflect on the cards immediately.
  const effectiveTokens = designTokens ?? {
    primaryColor,
    secondaryColor,
    tertiaryColor,
    fontFamily,
    primaryFontFamily: primaryFont,
    secondaryFontFamily: secondaryFont,
    forthColor: designTokens?.forthColor ?? forthColor
  };


  const effectiveTemplateName = ((designTokens?.templateName ? designTokens?.templateName : templateName) || "").toLowerCase();
  const isDefaultTheme = effectiveTemplateName === "default";
  const defaultBorderClass = isDefaultTheme ? "grid-default-theme-border" : "";

  const contentHoverBg = useMemo(() => {
    return getHoverColor(
      effectiveTokens?.forthColor,
      effectiveTokens?.secondaryColor,
      brand_color,
    );
  }, [brand_color, effectiveTokens?.primaryColor, effectiveTokens?.secondaryColor]);
  return (
    <div
      className={`card-link card-line w-100${isMobileView ? " mobile-view" : ""}`}
    >
      {isDefaultTheme && (
        <style>{`
          .uptodate-wrapper .grid-default-theme-border { border: 1px solid #ccc !important; }
          .uptodate-wrapper .line-template-card-content { background-color: #fff !important; }
          .uptodate-wrapper .line-template-card.has-image { background-color: #fff !important; }
          /* Hover background for default theme */
          .uptodate-wrapper .grid-default-theme-border .line-template-card:hover,
          .uptodate-wrapper .grid-default-theme-border .line-template-card:hover .line-template-card-content {
            background-color: #f5f5f7 !important;
          }
        `}</style>
      )}
      {isDashboard ? (
        <div
          className={`creator-card ${defaultBorderClass}`}
          style={{
            backgroundColor: isDefaultTheme
              ? "#fff"
              : effectiveTokens?.forthColor,
            boxShadow: effectiveTokens?.primaryColor ? "none" : undefined,
            paddingBottom: "0px",
          }}
        >
          <div className={`line-template-card ${imageUrl ? "has-image" : ""}`}>
            <div
              className="line-template-card-content"
              style={{
                border: effectiveTokens?.secondaryColor ? "none" : "",
                ["--content-hover-bg" as any]: isDashboard ? "#ebebef" : contentHoverBg,
              }}
              onClick={(e) => {
                if (isDragging) {
                  e.stopPropagation();
                  return;
                }
                // On the dashboard, editing should only be triggered via the edit icon button,
                // not by clicking anywhere on the card. So we only use this click handler
                // for non-dashboard contexts (e.g. live page interactions).
                if (
                  !isDashboard &&
                  (!editIndex || editIndex !== card.id)
                ) {
                  e.stopPropagation();
                  handleEditClick(e);
                }
              }}
            >
              {isLayoutCropProcessing && (
                <div className="line-template-processing-overlay">
                  <Spin size="small" />
                  <span className="line-template-processing-text">
                    Processing Image...
                  </span>
                </div>
              )}
              {isDashboard && (
                <div className="line-template-actions">
                  {isEditing ? (
                    <>
                      <button
                        className="line-template-save-button"
                        disabled={isImageBusy}
                        style={
                          isImageBusy
                            ? { opacity: 0.4, cursor: "not-allowed" }
                            : undefined
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSave(e);
                        }}
                      >
                        <FiCheck size={20} />
                      </button>
                      <button
                        className="line-template-cancel-button"
                        disabled={isImageBusy}
                        style={
                          isImageBusy
                            ? { opacity: 0.4, cursor: "not-allowed" }
                            : undefined
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelSave();
                        }}
                      >
                        <FiX size={20} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="line-template-option-button drag-handle"
                        {...(dragHandleProps || {})}
                        onClick={(e) => {
                          // prevent drag handle clicks from toggling edit mode
                          e.stopPropagation();
                        }}
                      >
                        <MdDragIndicator
                          size={20}
                          // className="drag-handle"
                          style={{ cursor: "grab", color: "black" }}
                        />
                      </button>
                      <button
                        className="line-template-edit-button"
                        onClick={(e) => {
                          if (isDashboard) {
                            e.stopPropagation();
                            handleEditClick(e);
                          }
                        }}
                      >
                        <FiEdit2 size={20} style={{ color: "black" }} />
                      </button>
                    </>
                  )}
                  {isEditing && (
                    <button
                      className="line-template-delete-button"
                      disabled={isImageBusy}
                      style={
                        isImageBusy
                          ? { opacity: 0.4, cursor: "not-allowed" }
                          : undefined
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <FiTrash2 size={20} />
                    </button>
                  )}
                </div>
              )}

              {showDeleteConfirm && (
                <DeleteConfirmation
                  setShowDeleteConfirm={setShowDeleteConfirm}
                  setEditIndex={setEditIndex}
                  handleDelete={handleDelete}
                />
              )}

              {!imageUrl && !isEditing ? null : (
              <div className={`line-image-wrapper${imageUrl ? "" : " is-empty"}`}>
                {imageUrl ? (
                  <div className="line-template-logo-placeholder">
                    <div className="line-template-logo-container">
                      <img
                        src={imageUrl}
                        alt="logo"
                        className="line-template-logo"
                        loading="lazy"
                        decoding="async"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          ...lineCoverStyle(card),
                          pointerEvents: isEditing ? "auto" : "none",
                        }}
                      />
                      {isEditing && (
                        <div
                          className="line-template-logo-overlay"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="line-template-logo-overlay-actions">
                            <FiTrash2
                              size={20}
                              className="line-template-overlay-icon remove"
                              onClick={handleRemoveImage}
                              aria-label="Remove link image"
                            />
                            {isImageBusy ? (
                              <Spin
                                size="small"
                                className="line-template-overlay-icon spinner"
                              />
                            ) : (
                              <FiEdit2
                                size={20}
                                className="line-template-overlay-icon add"
                                onClick={handleOpenLinkCropper}
                                aria-label="Crop & Zoom"
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="line-template-logo-placeholder" style={{backgroundColor: "#f5f5f5"}}>
                    <Upload {...uploadProps}>
                      <div className="upload-area">
                        {isImageBusy ? (
                          <Spin
                            size="small"
                            className="line-template-upload-spinner"
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            className="line-template-icon"
                          >
                            <path
                              d="M12 4v16m-8-8h16"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </div>
                    </Upload>
                  </div>
                )}
              </div>
              )}

              <div
                className={`line-template-left-section${
                  imageUrl ? " has-image" : " no-image"
                }`}
              >
                <div
                  className={
                    isEditing
                      ? `line-template-text-section w-100 line-title-center ${
                          isEditing && !imageUrl && "input-section"
                        }`
                      : "line-template-text-section w-100"
                  }
                >
                  {!!validationError && (
                    <Popover
                      content={
                        <span className="errorMsg">{validationError}</span>
                      }
                      open={!!validationError}
                      placement="top"
                    ></Popover>
                  )}
                  <div className="line-template-title-section">
                    {isEditing ? (
                      <Input
                        type="text"
                        value={title}
                        placeholder="Enter title"
                        maxLength={MAX_LINK_NAME_CHARS}
                        showCount
                        onChange={(e) => {
                          setTitle(e.target.value);
                          if (validationError) setValidationError("");
                        }}
                        className="line-template-title-input"
                        autoFocus
                      />
                    ) : (
                      <>
                        {title ? (
                          <div
                            className={
                              imageUrl
                                ? "line-template-text-center"
                                : "line-template-text-center"
                            }
                          >
                            <p
                              className={`line-template-title ${
                                !imageUrl ? "no-image" : ""
                              }`}
                              style={{ color: isDashboard ? undefined : effectiveTokens?.secondaryColor}}
                            >
                              {title}
                            </p>
                          </div>
                        ) : (
                          <div className="line-template-text-center">
                            <p
                              className={`line-template-title title-placeholder ${
                                !imageUrl ? "no-image" : ""
                              }`}
                            >
                              Title goes here
                            </p>
                          </div>
                        )}
                      </>
                      // <h1 className="line-template-title">{title}</h1>
                    )}
                  </div>
                  {validationErrorLink && (
                    <Popover
                      content={
                        <span className="errorMsg">{validationErrorLink}</span>
                      }
                      open={!!validationErrorLink}
                      placement="top"
                    ></Popover>
                  )}
                  {
                    isEditing ? (
                      <div className="line-template-link-section">
                        <Input
                          type="text"
                          value={link}
                          placeholder="Enter Link"
                          onChange={(e) => {
                            const value = e.target.value;
                            setLink(value);
                            if (validationErrorLink) setValidationErrorLink("");
                          }}
                          // onChange={(e) => setLink(e.target.value)}
                          className="line-template-link-input"
                        />
                      </div>
                    ) : null
                    // <p className="line-template-link">{link}</p>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <a
          // href={card.link_url.startsWith('http') ? card.link_url : `https://${card.link_url}`}
          href={addHttpToLink(card.link_url)}
          // href={card.link_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            handleRedirect(
              card.id,
              card.image_url,
              "links-section",
              card.link_name,

              componentName,
              "",
              pageFrom

              // true
            );
          }}
          style={{color: isDefaultTheme? "#FFFFFF" : effectiveTokens?.forthColor, backgroundColor: isDefaultTheme? "#FFFFFF" : effectiveTokens?.forthColor}}
        >
          <div
            className={`creator-card ${defaultBorderClass}`}
            style={{
              backgroundColor: isDefaultTheme
                ? "#fff"
                : (effectiveTokens?.forthColor ? effectiveTokens?.forthColor : brand_color),
              boxShadow: effectiveTokens?.secondaryColor ? "none" : undefined,
              paddingBottom: "0px",
              ...(isDefaultTheme && {border: "1px solid #ccc"})
            }}
          >
            <div
              className={`line-template-card ${imageUrl ? "has-image" : ""} `}
            >
              <div
                className={`line-template-card-content ${isDefaultTheme ? "line-template-card-content-mobile" : ""}`}
                style={{
                border: effectiveTokens?.secondaryColor ? "none" : "",
                ["--content-hover-bg" as any]: isDashboard ? "#ebebef" : contentHoverBg,
                }}
                onClick={(e) => {
                  if (isDragging) {
                    e.stopPropagation();
                    return;
                  }
                  if (isDashboard && (!editIndex || editIndex !== card.id)) {
                    e.stopPropagation();
                    handleEditClick(e);
                  }
                }}
              >
                {isLayoutCropProcessing && (
                  <div className="line-template-processing-overlay">
                    <Spin size="small" />
                    <span className="line-template-processing-text">
                      Processing...
                    </span>
                  </div>
                )}
                {isDashboard && (
                  <div className="line-template-actions">
                    {isEditing ? (
                      <>
                        <button
                          className="line-template-save-button"
                          disabled={isImageBusy}
                          style={
                            isImageBusy
                              ? { opacity: 0.4, cursor: "not-allowed" }
                              : undefined
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSave(e);
                          }}
                        >
                          <FiCheck size={20} />
                        </button>
                        <button
                          className="line-template-cancel-button"
                          disabled={isImageBusy}
                          style={
                            isImageBusy
                              ? { opacity: 0.4, cursor: "not-allowed" }
                              : undefined
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelSave();
                          }}
                        >
                          <FiX size={20} />
                        </button>
                      </>
                    ) : (
                      <button
                        className="line-template-edit-button"
                        onClick={(e) => {
                          if (isDashboard) {
                            e.stopPropagation();
                            handleEditClick(e);
                          }
                        }}
                      >
                        <FiEdit2 size={20} style={{ color: "black" }} />
                      </button>
                    )}
                    {isEditing && (
                      <button
                        className="line-template-delete-button"
                        disabled={isImageBusy}
                        style={
                          isImageBusy
                            ? { opacity: 0.4, cursor: "not-allowed" }
                            : undefined
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(true);
                        }}
                      >
                        <FiTrash2 size={20} />
                      </button>
                    )}
                  </div>
                )}

                {showDeleteConfirm && (
                  <DeleteConfirmation
                    setShowDeleteConfirm={setShowDeleteConfirm}
                    setEditIndex={setEditIndex}
                    handleDelete={handleDelete}
                  />
                )}

                {!imageUrl && !isEditing ? null : (
                <div className={`line-image-wrapper${imageUrl ? "" : " is-empty"}`}>
                  {imageUrl ? (
                    <div className="line-template-logo-placeholder">
                      <div className="line-template-logo-container">
                        <img
                          src={imageUrl}
                          alt="logo"
                          className="line-template-logo"
                          loading="lazy"
                          decoding="async"
                          style={lineCoverStyle(card)}
                        />
                        {isEditing && (
                          <div
                            className="line-template-logo-overlay"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="line-template-logo-overlay-actions">
                              <FiTrash2
                                size={20}
                                className="line-template-overlay-icon remove"
                                onClick={handleRemoveImage}
                                aria-label="Remove link image"
                              />
                              {isImageBusy ? (
                                <Spin
                                  size="small"
                                  className="line-template-overlay-icon spinner"
                                />
                              ) : (
                                <FiEdit2
                                  size={20}
                                  className="line-template-overlay-icon add"
                                  onClick={handleOpenLinkCropper}
                                  aria-label="Crop & Zoom"
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="line-template-logo-placeholder"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Upload {...uploadProps}>
                        <div className="upload-area">
                          {isImageBusy ? (
                            <Spin
                              size="small"
                              className="line-template-upload-spinner"
                            />
                          ) : (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              className="line-template-icon"
                            >
                              <path
                                d="M12 4v16m-8-8h16"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                        </div>
                      </Upload>
                    </div>
                  )}
                </div>
                )}

                <div
                  className={`line-template-left-section${
                    imageUrl ? " has-image" : " no-image"
                  }`}
                >
                  <div
                    className={
                      isEditing
                        ? `line-template-text-section w-100 line-title-center ${
                            isEditing && !imageUrl && "input-section"
                          }`
                        : "line-template-text-section w-100"
                    }
                  >
                    {!!validationError && (
                      <Popover
                        content={
                          <span className="errorMsg">{validationError}</span>
                        }
                        open={!!validationError}
                        placement="top"
                      ></Popover>
                    )}
                    <div className="line-template-title-section">
                      {isEditing ? (
                        <Input
                          type="text"
                          value={title}
                          placeholder="Enter title"
                          maxLength={MAX_LINK_NAME_CHARS}
                          showCount
                          onChange={(e) => {
                            setTitle(e.target.value);
                            if (validationError) setValidationError("");
                          }}
                          className="line-template-title-input"
                          autoFocus
                        />
                      ) : (
                        <>
                          {title ? (
                            <div
                              className={
                                imageUrl
                                  ? "line-template-text-center"
                                  : "line-template-text-center"
                              }
                            >
                              <p
                                className={`line-template-title ${
                                  !imageUrl ? "no-image" : ""
                                }`}
                                style={{ color: isDashboard ? undefined : effectiveTokens?.secondaryColor, fontFamily: effectiveTokens?.secondaryFontFamily }}
                              >
                                {title}
                              </p>
                            </div>
                          ) : (
                            <div className="line-template-text-center">
                              <p
                                className={`line-template-title title-placeholder ${
                                  !imageUrl ? "no-image" : ""
                                }`}
                              >
                                Title goes here
                              </p>
                            </div>
                          )}
                        </>
                        // <h1 className="line-template-title">{title}</h1>
                      )}
                    </div>
                    {validationErrorLink && (
                      <Popover
                        content={
                          <span className="errorMsg">
                            {validationErrorLink}
                          </span>
                        }
                        open={!!validationErrorLink}
                        placement="top"
                      ></Popover>
                    )}
                    {
                      isEditing ? (
                        <div className="line-template-link-section">
                          <Input
                            type="text"
                            value={link}
                            placeholder="Enter Link"
                            onChange={(e) => {
                              const value = e.target.value;
                              setLink(value);
                              if (validationErrorLink)
                                setValidationErrorLink("");
                            }}
                            className="line-template-link-input"
                          />
                        </div>
                      ) : null
                      // <p className="line-template-link">{link}</p>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a>
      )}
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            void handleSelectImageFile(file);
          }
          event.target.value = "";
        }}
      />
      <ImageCropperModal
        open={cropperOpen}
        imageSrc={cropperImageSrc}
        preset={LINK_LINE_CROP}
        initialCropState={initialCropState}
        saving={uploading || isLoadingOriginal}
        title="Edit Link Image"
        onCancel={handleCropperCancel}
        onSave={(file, cropState) => void handleCroppedImageSave(file, cropState)}
        onReplace={handleCropReplace}
      />
    </div>
  );
};

export default LineTemplate;
